import { EditOutlined, QuestionCircleFilled } from "@ant-design/icons"
import { Button, DatePicker, Form, Input, InputNumber, Select, Switch, Tabs, Tooltip } from "antd"
import TextArea from "antd/es/input/TextArea"
import dayjs, { Dayjs } from "dayjs"
import { t } from "i18next"
import React, { useState } from "react"

import SeoForm from "@/(admin)/components/seo/seo-form"
import InclusionForm from "@/(admin)/components/tour/inclusion-form"
import TimelineForm from "@/(admin)/components/tour/timeline-form"
import CKEditorComponent from "@/lib/config/lib/Ckeditor"
import { removeAccents } from "@/lib/utils"
import { BikeTypeOption, TourFormProps } from "@/types/types"
import { slugPattern } from "@/utils/validationPatterns"
import { checkSlug } from "../services/tours"

const TourForm: React.FC<TourFormProps> = ({ form, onFinish, bikeTypes = [], loadingBikeTypes = false }) => {
  const [isEditableSlug, setIsEditableSlug] = useState(true)
  const showBikeType = Form.useWatch("show_bike_type", form) || false

  const handleNameBlur = async (e: React.FocusEvent<HTMLInputElement>) => {
    const nameValue = e.target.value.trim()
    if (!nameValue) {
      form?.setFields([{ name: "slug", errors: [] }])
      return
    }

    const slugValue = removeAccents(nameValue)

    const isSlugUnique = async (slug: string): Promise<boolean> => {
      try {
        const response = await checkSlug(slug)

        return response.data.length === 0
      } catch {
        return false
      }
    }
    const isUnique = await isSlugUnique(slugValue)

    if (!isUnique) {
      form.setFieldsValue({ slug: slugValue })
      form.setFields([{ name: "slug", errors: [t("Slug already exists. Please choose another slug.")] }])
    } else {
      form.setFieldsValue({ slug: slugValue })
      form.setFields([{ name: "slug", errors: [] }])
    }
  }

  const generalInput = [
    {
      name: "description",
      label: t("Short Description"),
      children: <TextArea size="large" autoSize={{ minRows: 3, maxRows: 6 }} placeholder="About this tour" />,
      gridSpan: 3,
    },
    {
      name: "price_overview",
      label: t("Price Overview"),
      required: true,
      children: (
        <InputNumber size="large" className="w-full" min={0} suffix="$/person" placeholder="Price per person" />
      ),
    },

    {
      required: true,
      name: "duration",
      label: t("Duration"),
      children: <Input size="large" placeholder="7 days" />,
    },
    {
      required: true,
      name: "location",
      label: t("Location"),
      children: <Input size="large" placeholder="Vietnam" />,
    },

    {
      required: true,
      name: "distance",
      label: t("Distance"),
      children: <Input size="large" placeholder="100 KM" />,
    },

    {
      name: "difficulty",
      label: t("Difficulty"),
      children: <Input size="large" placeholder="Easy/Moderate/Endurance" />,
    },
    {
      name: "elevation",
      label: t("Elevation"),
      children: <Input size="large" placeholder="+8,900 M -9,000 M" />,
    },
    {
      name: "price_note",
      label: t("Price Note"),
      children: <Input size="large" placeholder="Price Note" />,
      gridSpan: 3,
    },
  ]

  const tabs = [
    {
      key: "over_view",
      label: t("Overview"),
      children: (
        <div className="p-3 border border-t-0 rounded-lg rounded-t-none border-[#f0f0f0">
          <Form.Item
            name="over_view_label"
            layout="horizontal"
            label={<span className="text-base font-medium text-center">{t("Label")}</span>}
          >
            <Input size="large" />
          </Form.Item>
          <Form.Item
            name="over_view_description"
            label={<span className="text-base font-medium">{t("Description")}:</span>}
          >
            <CKEditorComponent
              value={form.getFieldValue("over_view_description")}
              onChange={(content: string) => {
                form.setFieldsValue({ over_view_description: content })
              }}
            />
          </Form.Item>
        </div>
      ),
    },
    {
      key: "highlights",
      label: t("Highlight"),
      children: (
        <Form.Item name="highlights">
          <CKEditorComponent
            value={form.getFieldValue("highlights")}
            onChange={(content: string) => {
              form.setFieldsValue({ highlights: content })
            }}
          />
        </Form.Item>
      ),
    },
    {
      key: "itinerary",
      label: t("Itinerary"),
      children: (
        <Form.Item name="itinerary">
          <TimelineForm form={form} />
        </Form.Item>
      ),
    },
    {
      key: "tour_detail",
      label: t("Tour Detail"),
      children: (
        <Form.Item name="tour_detail">
          <CKEditorComponent
            value={form.getFieldValue("tour_detail")}
            onChange={(content: string) => {
              form.setFieldsValue({ tour_detail: content })
            }}
          />
        </Form.Item>
      ),
    },
  ]

  return (
    <Form
      form={form}
      className="flex flex-col w-[77%] "
      name="tourForm"
      onFinish={onFinish}
      autoComplete="off"
      layout="vertical"
      initialValues={{
        is_new: false,
        is_most_popular: false,
        show_bike_type: false,
        bike_types: [],
      }}
    >
      <Form.Item
        name="name"
        label={<span className="text-base font-medium">{t("Tour Name")}</span>}
        rules={[{ required: true, message: t("Please enter a tour name!") }]}
      >
        <Input
          placeholder={t("Add Name")}
          onBlur={handleNameBlur}
          onChange={e => {
            form.setFieldValue("metaTitle", e.target.value)
            if (form.getFieldValue("excerpt") == undefined) {
              form.setFieldValue("metaDescription", e.target.value)
            }
          }}
        />
      </Form.Item>

      <div className="flex w-full items-center gap-2">
        <Form.Item
          className="w-1/2"
          name="slug"
          label={<span className="text-base font-medium">{t("Slug")}</span>}
          rules={[
            {
              pattern: slugPattern,
              message: t("Slug must match the following: 'A-Z, a-z, 0-9, - ,_ ,. ,~'"),
            },
          ]}
          extra={<span className="text-sm ">{t("May not need to be entered (automatically render by name)")}</span>}
        >
          <Input disabled={isEditableSlug} placeholder={t("Slug")} className="" />
        </Form.Item>

        <Button
          icon={<EditOutlined />}
          color={!isEditableSlug ? "primary" : "default"}
          variant="outlined"
          onClick={() => setIsEditableSlug(!isEditableSlug)}
          className="mb-4"
        >
          {t("Edit")}
        </Button>
      </div>

      <div className="flex flex-row gap-5">
        <Form.Item
          name="is_new"
          label={
            <div className="text-base font-medium">
              {t("Is New Tour")}?{" "}
              <Tooltip
                title={t(`Show label 'New' tour on the website`)}
                className="text-nowrap"
                styles={{ body: { whiteSpace: "nowrap", width: "fit-content" } }}
              >
                <QuestionCircleFilled style={{ fontSize: 18 }} />
              </Tooltip>
            </div>
          }
          className="w-1/2"
        >
          <Switch />
        </Form.Item>
        <Form.Item
          name="is_most_popular"
          label={<div className="text-base font-medium"> {t("Is Most Popular")}? </div>}
          className="w-1/2"
        >
          <Switch />
        </Form.Item>
      </div>

      <div className="grid grid-cols-3 gap-5 mb-4">
        <Form.Item
          name="start_time"
          label={<span className="text-base font-medium">{t("Start Time")}</span>}
          className="col-span-1"
          getValueFromEvent={(value: Dayjs | null) => value?.toISOString()}
          getValueProps={(value: string | undefined) => ({
            value: value ? dayjs(value) : null,
          })}
        >
          <DatePicker
            showTime
            format="YYYY-MM-DD HH:mm:ss"
            size="large"
            className="w-full"
            placeholder={t("Select start time")}
          />
        </Form.Item>
        <Form.Item
          name="show_bike_type"
          label={<span className="text-base font-medium">{t("Show Bike Type")}</span>}
          className="col-span-1"
        >
          <Switch
            onChange={checked => {
              if (!checked) {
                form.setFieldValue("bike_types", [])
              }
            }}
          />
        </Form.Item>
        {showBikeType && (
          <Form.Item name="bike_types" label={<span className="text-base font-medium">{t("Bike Types")}</span>}>
            <Select
              mode="multiple"
              size="large"
              placeholder={t("Select bike types")}
              loading={loadingBikeTypes}
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) => {
                const label = typeof option?.label === "string" ? option.label : String(option?.label ?? "")
                return label.toLowerCase().includes(input.toLowerCase())
              }}
              maxTagCount="responsive"
            >
              {bikeTypes.map((bikeType: BikeTypeOption) => (
                <Select.Option key={bikeType.id} value={bikeType.id} label={bikeType.attributes.name}>
                  {bikeType.attributes.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        )}
      </div>

      <span className="text-base font-medium pb-2">Thông tin chung</span>
      <div className="grid grid-cols-3 gap-5 p-3 border rounded-lg mb-4">
        {generalInput.map(item => (
          <Form.Item
            key={item.name}
            name={item.name}
            required={item.required}
            rules={[{ required: item.required, message: "" }]}
            label={<div className="text-base font-medium">{item.label}</div>}
            className="mb-0"
            style={{ gridColumn: `span ${item.gridSpan} / span ${item.gridSpan}` }}
          >
            {item.children}
          </Form.Item>
        ))}

        <Form.Item
          name="inclusions"
          label={<span className="text-base font-medium">{t("Inclusions")}</span>}
          className="col-span-3 mb-0"
        >
          <InclusionForm form={form} />
        </Form.Item>
      </div>

      <Tabs
        type="card"
        items={tabs}
        animated={{ tabPane: true }}
        size="large"
        tabBarStyle={{ marginBottom: "0px", borderBottom: "none" }}
      />

      <SeoForm />
    </Form>
  )
}

export default TourForm
